<template>
    <sidebar @close="$emit('close')">
        <template v-slot:title>
            {{ plan.name }}
        </template>

        <template v-slot:header>
            <div class="row mb-1">
                <div class="col">
                    <span class="text-muted">
                        {{ isCustom ? 'Custom Plan' : `${plan.state_pretty || 'All States'} · Global Plan` }}
                        <span v-if="!clientPlan.is_active">&nbsp;&middot; Discontinued</span>
                    </span>
                </div>
            </div>

            <div class="mb-1" v-if="clientPlan.is_active">
                <button v-if="isCustom" class="btn btn-outline-primary mr-1" type="button" @click.prevent="editPlanOpen = true">
                    <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                    Edit Plan
                </button>
                <button class="btn btn-outline-primary" type="button" @click.prevent="discontinueOpen = true">Discontinue</button>
            </div>

            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href @click.prevent="activeTab = 'main'" role="tab" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'history'}" href @click.prevent="activeTab = 'history'" role="tab" :aria-selected="activeTab == 'history'">Change History</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <template v-if="activeTab == 'main'">
                <info-widget :collapsible="false">
                    <template v-slot:title>Availability</template>

                    <template v-slot:headerbutton v-if="clientPlan.is_active">
                        <button type="button" class="btn btn-outline-primary" @click.prevent="positionsOpen = true">
                            <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                            Edit
                        </button>
                    </template>

                    <slot>
                        <div class="info-widget-empty p-2" v-if="clientPlan.all_positions">
                            Available to all companies in this client.
                        </div>
                        <div v-else-if="clientPlan.positions.length" class="p-1">
                            <table class="table css-table widget-table table-fixed">
                                <thead>
                                    <tr>
                                        <th>Company</th>
                                        <th>Position</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="position in sortedPositions" :key="position.id">
                                        <td class="truncate-wrapper">{{ companyName(position) }}</td>
                                        <td class="truncate-wrapper">{{ position.terminal_value }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="info-widget-empty p-2" v-else>
                            No positions selected.
                        </div>
                    </slot>
                </info-widget>

                <plan-overview :plan="plan" />
            </template>

            <!-- Keyed on the plan so an edit reloads it. -->
            <plan-change-history
                v-if="activeTab == 'history'"
                :key="plan.updated_on"
                :url="`/clients/${client.id}/settings/accrual-plans/${clientPlan.id}/history`"
            />

            <plan-modal
                v-if="editPlanOpen"
                :plan="plan"
                :client="client"
                :client-plan-id="clientPlan.id"
                @updated="$emit('updated')"
                @close="editPlanOpen = false"
            />

            <positions-modal
                v-if="positionsOpen"
                :client="client"
                :client-plan="clientPlan"
                @updated="$emit('updated')"
                @close="positionsOpen = false"
            />

            <discontinue-modal
                v-if="discontinueOpen"
                :client="client"
                :client-plan="clientPlan"
                @updated="$emit('updated')"
                @close="discontinueOpen = false"
            />
        </slot>
    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import PlanModal from '@/pages/admin/accruals/components/PlanModal'
import PlanOverview from '@/pages/admin/accruals/components/PlanOverview'
import PlanChangeHistory from '@/pages/admin/accruals/components/PlanChangeHistory'
import PositionsModal from './PositionsModal'
import DiscontinueModal from './DiscontinueModal'

export default {
    components: {Sidebar, InfoWidget, PlanModal, PlanOverview, PlanChangeHistory, PositionsModal, DiscontinueModal, },
    props: ['client', 'clientPlan', ],
    emits: ['close', 'updated', ],
    data() {
        return {
            activeTab: 'main',
            editPlanOpen: false,
            positionsOpen: false,
            discontinueOpen: false,
        }
    },
    computed: {
        plan() {
            return this.clientPlan.accrual_plan
        },
        isCustom() {
            return !!this.plan.client_id
        },
        sortedPositions() {
            return [...this.clientPlan.positions].sort((a, b) =>
                this.companyName(a).localeCompare(this.companyName(b)) || a.terminal_value.localeCompare(b.terminal_value)
            )
        },
    },
    methods: {
        companyName(position) {
            const company = this.client.companies.find(c => c.id == position.company_id)
            return company ? company.name : ''
        },
    },
}
</script>
